import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { PaymentDateFormat } from "../helpers/CommonHelpers.js";
import { EmptyCartHTML, clearUserCartLocalStorage, calculateGrandTotal, OrderSummary, PaymentPrintReceipt } from "../components/ReusableComponents";
import { cartToItems, getCurrency, pushEcommEvent } from "../utils/ga4";
const handlePrint = () => {
    const printContent = document.getElementById("thankyou-print-section").innerHTML;
    const win = window.open("", "_blank", "width=900,height=650");
    win.document.write(`
        <html>
            <head>
                <title>Receipt</title>
                <link rel="stylesheet" href="/wp-content/themes/dom/assets/css/print.css" media="print" /> 
            </head>

            <body>
                ${printContent}
            </body>
        </html>
    `);
    win.document.close();
    win.onload = () => {
        win.focus();
        win.print();
    };
};

const ThankYou = () => {
    const im_custom = window.im_ecom || {};
    const supportEmail = im_custom.support_email ?? "";
    const navigate = useNavigate();

    const [grandTotal, setGrandTotal] = useState(0);
    const [taxAmount, setTaxAmount] = useState(0);
    const purchaseSentRef = useRef(false);

    // Track if cart has been initialized (loaded from localStorage or has content)
    const isInitializedRef = useRef(false);

    const [userCart, setUserCart] = useState({
        users: [],
        attendees: [],
        activePlan: null,
        activeEvent: null,
        isAgreed: "0",
    });

    const [verifyUrl, setVerifyUrl] = useState(null);
    const [paymentStatus, setPaymentStatus] = useState(null); // default to success


    const [loading, setLoading] = useState(true);

    const [orderId, setOrderId] = useState('');
    const [paymentMethodDetails, setPaymentMethodDetails] = useState(null);
    const [paymentDate, setPaymentDate] = useState(null);


    useEffect(() => {
        // Load cart data
        const saved = localStorage.getItem("user_cart");
        if (saved) {
            clearUserCartLocalStorage();
        } else {
            window.location.href = '/';
        }
        const savedParse = saved ? JSON.parse(saved) : null;
        if (savedParse) {
            console.log("Loaded saved cart:", savedParse);
            setUserCart(savedParse);
            // Mark as initialized if cart has content
            if (savedParse.activePlan || savedParse.activeEvent) {
                isInitializedRef.current = true;
            }
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        // console.log("User cart changed:", userCart);
        setGrandTotal(calculateGrandTotal(userCart, im_custom));

        // Mark as initialized when cart gets content
        if (!isInitializedRef.current && (userCart.activePlan || userCart.activeEvent)) {
            isInitializedRef.current = true;
        }

        // Only persist if cart is initialized and has content
        if (!isInitializedRef.current) return;
        if (!userCart.activePlan && !userCart.activeEvent) return;

        // localStorage.setItem("user_cart", JSON.stringify(userCart));
    }, [userCart]);

    useEffect(() => {
        // Payment is already confirmed before navigation to this page
        // (polling happens in PaymentForm component)
        setPaymentStatus('succeeded');
    }, []);

    // GA4 purchase event once payment is confirmed
    useEffect(() => {
        if (paymentStatus !== 'succeeded') return;
        if (purchaseSentRef.current) return;
        const items = cartToItems(userCart, im_custom);
        if (!items.length) return;
        const currency = getCurrency(im_custom);
        const value = Number(grandTotal || 0) + Number(taxAmount || 0);
        const transactionId = orderId || userCart.sessionID || 'pending';
        pushEcommEvent('purchase', {
            currency,
            transaction_id: transactionId,
            value,
            tax: Number(taxAmount || 0),
            items,
        });
        purchaseSentRef.current = true;
    }, [paymentStatus, grandTotal, taxAmount, orderId, userCart, im_custom]);

    // Fetch actual tax and grand total from Stripe invoice via payment status
    useEffect(() => {
        const fetchPaymentStatus = async () => {
            try {
                const sessionId = userCart.sessionID;
                if (!sessionId) {
                    console.log('No session ID found, using calculated totals');
                    return;
                }

                const base = window.location.origin || "";
                const endpoint = `/wp-json/dom/v1/payment/status?session_id=${encodeURIComponent(sessionId)}`;

                const res = await fetch(endpoint);
                if (!res.ok) {
                    console.warn('Failed to fetch payment status:', res.status);
                    return;
                }

                const data = await res.json();
                if (data && data.is_complete) {
                    // Update tax amount from Stripe
                    if (data.taxAmount !== undefined) {
                        setTaxAmount(data.taxAmount);
                    }
                    // Update grand total (subtotal without tax) from Stripe
                    // OrderSummary will add tax to this to get final total
                    // Prefer grandTotal - taxAmount over subtotal because subtotal only includes
                    // subscription items, not one-time invoice items (like events)
                    if (data.grandTotal !== undefined && data.taxAmount !== undefined) {
                        // Use grandTotal (with tax) minus tax to get subtotal (includes all items)
                        setGrandTotal(data.grandTotal - data.taxAmount);
                    } else if (data.subtotal !== undefined) {
                        // Fallback to subtotal if grandTotal is not available
                        setGrandTotal(data.subtotal);
                    }
                    if (data.paymentMethodDetails !== undefined) {
                        setPaymentMethodDetails(data.paymentMethodDetails);
                    }
                    if (data.paymentDate !== undefined) {
                        setPaymentDate(data.paymentDate);
                    }
                    if (data.invoiceId !== undefined) {
                        setOrderId(data.invoiceId);
                    }
                }
            } catch (error) {
                console.error('Error fetching payment status:', error);
                // Fall back to calculated totals if API fails
            }
        };

        // Only fetch if we have a session ID, cart is loaded, and cart has items
        if (userCart.sessionID && !loading && (userCart.activePlan || userCart.activeEvent)) {
            fetchPaymentStatus();
        }
    }, [userCart.sessionID, loading]);


    if (loading) {
        return (
            <div className="about-wrapper">
                <div className="about-container cart-main checkout-main thank-you-page">
                    <h1>Processing Payment...</h1>
                    <p className="public-desc">
                        Please wait while we confirm your payment. This may take a few moments.
                    </p>
                </div>
            </div>
        );
    }

    /* If cart is empty, show empty cart message */
    if (!userCart.activePlan && !userCart.activeEvent) {
        return (<EmptyCartHTML />);
    }

    return (
        <>
            <div style={{ display: "none" }}>
                <PaymentPrintReceipt im_custom={im_custom} userCart={userCart} orderId={orderId} grandTotal={grandTotal} estimatedTax={taxAmount}
                    paymentDate={paymentDate}
                    paymentMethodDetails={paymentMethodDetails}
                    receiptHeading="The Payment has succeeded"
                    receiptParagraph="You will receive an email confirmation shortly. Be sure to print this page for your records."
                />
            </div>
            <div className="about-wrapper">
                <div className="about-container cart-main checkout-main thank-you-page">
                    {verifyUrl && (
                        <div style={overlayStyle}>
                            <div style={popupStyle}>
                                <h3>Verify Your Bank Account</h3>
                                <iframe
                                    src={verifyUrl}
                                    title="ACH Verification"
                                    style={{
                                        width: "100%",
                                        height: "80vh",
                                        border: "none",
                                        borderRadius: "8px",
                                    }}
                                />
                                <button className="custom-button w-100 mt-3" onClick={() => setVerifyUrl(null)}>
                                    Close
                                </button>
                            </div>
                        </div>
                    )}


                    <div className="row justify-content-space-between">
                        <div className="col-lg-8">
                            {paymentStatus === "succeeded" && (
                                <>
                                    <h1>The Payment has succeeded.</h1>
                                    <p className="public-desc">
                                        You will receive an email confirmation shortly. Be sure to print
                                        this page for your records.
                                    </p>
                                </>
                            )}
                            {paymentStatus === "canceled" && (
                                <>
                                    <h1>The Payment has been canceled.</h1>
                                    <p className="public-desc">
                                        Unfortunately, your payment could not be completed. Please try again or contact support.
                                    </p>
                                </>
                            )}
                            {paymentStatus === "requires_action" && (
                                <>
                                    <h1>Payment Pending...</h1>
                                    <p className="public-desc">
                                        The Payment is currently being processed.
                                    </p>
                                </>
                            )}
                            {paymentStatus === "requires_capture" && (
                                <>
                                    <h1>Payment Pending...</h1>
                                    <p className="public-desc">
                                        The Payment is currently being processed.
                                    </p>
                                </>
                            )}
                            {paymentStatus === "requires_confirmation" && (
                                <>
                                    <h1>Payment Pending...</h1>
                                    <p className="public-desc">
                                        Payment is currently being processed.
                                    </p>
                                </>
                            )}
                            {paymentStatus === "requires_payment_method" && (
                                <>
                                    <h1>Payment failed.</h1>
                                    <p className="public-desc">
                                        Unfortunately, we couldn’t process your payment. Please try again with another card.
                                    </p>
                                </>
                            )}
                            {paymentStatus === "invoice_request" && (
                                <>
                                    <h1>Invoice Requested – Payment Pending.</h1>
                                    <p className="public-desc">
                                        Your order is received. Payment is pending via invoice. We’ll email you further instructions.
                                    </p>
                                </>
                            )}
                            {paymentStatus === "register_success" && (
                                <>
                                    <h1>Thanks for Joining Us</h1>
                                    <p className="public-desc">
                                        Your registration is complete. You’re all set to begin using the site and exploring everything we offer.
                                    </p>
                                </>
                            )}

                            {orderId ? (
                                <p className="public-desc">Order ID#: {orderId}</p>
                            ) : null}
                            {paymentDate ? (
                                <p className="public-desc"><PaymentDateFormat input={paymentDate} /></p>
                            ) : null}

                            {userCart.activePlan?.plan_label && (
                                <div className="standard">
                                    <div>
                                        <h3>
                                            {userCart.activePlan?.plan_label && `Plan : ${userCart.activePlan.plan_label}`}
                                        </h3>
                                    </div>
                                </div>
                            )}

                            {userCart.activePlan && userCart.activePlan.plan_id && (
                                <div className="research">
                                    <div className="row">
                                        <div className="col-2 col-lg-3 col-xl-2 mb-2 mb-lg-4">
                                            <div className="image-wrap">
                                                <img src="/wp-content/themes/dom/assets/img/atlas-laptop.webp"
                                                    alt="Atlas Membership" />
                                            </div>
                                        </div>
                                        <div className="col-3 col-lg-9 col-xl-4 mb-2 mb-lg-4">
                                            <div className="research-one">
                                                <div>
                                                    <h3>
                                                        Atlas <br /> Membership
                                                    </h3>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-sm-4 col-lg-6 col-xl-3 mb-2 mb-lg-4">
                                            <div className="research-one">
                                                <div>
                                                    {(() => {
                                                        const totalUsers = userCart.users?.length || 0;
                                                        const defaultUsers = userCart.users?.filter((u) => u.isDefault === "1").length || 0;
                                                        const additionalUsers = userCart.users?.filter((u) => u.isDefault === "0").length || 0;
                                                        const assignedUsers =
                                                            userCart.users?.filter(
                                                                (u) =>
                                                                    (u.firstName && u.firstName.trim() !== "") &&
                                                                    (u.lastName && u.lastName.trim() !== "") &&
                                                                    (u.email && u.email.trim() !== "")
                                                            ).length || 0;

                                                        const unassignedUsers = totalUsers - assignedUsers;

                                                        return (
                                                            <>
                                                                <div className="lnfo mb-1">
                                                                    {assignedUsers} of {totalUsers}{" "}
                                                                    {totalUsers === 1 ? "User" : "Users"} Assigned
                                                                </div>

                                                                {unassignedUsers > 0 ? (
                                                                    <p>
                                                                        You have {unassignedUsers} unassigned{" "}
                                                                        {unassignedUsers === 1 ? "user" : "users"}. You can assign them later by{" "}
                                                                        <a href={`mailto:${supportEmail}?subject=Assign%20Users%20Request`}>emailing us</a>.
                                                                    </p>
                                                                ) : (
                                                                    <p>All users are assigned</p>
                                                                )}
                                                                {/* Assigned users list */}
                                                                <div className="assigned-attendees mt-2">
                                                                    <h5>Assigned Users:</h5>
                                                                    {userCart.users
                                                                        .filter((a) => a.leaveSeatUnassigned === "0")
                                                                        .map((a, i) => (
                                                                            <div key={i}>
                                                                                {a.firstName} {a.lastName} ({a.email})
                                                                            </div>
                                                                        ))}

                                                                    {userCart.users.filter((a) => a.leaveSeatUnassigned === "0").length === 0 && (
                                                                        <div>No assigned users.</div>
                                                                    )}
                                                                </div>
                                                            </>
                                                        );
                                                    })()}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-sm-3 col-lg-6 col-xl-3 mb-2 mb-lg-4">
                                            <div className="research-one">
                                                <div className="w-100">
                                                    <a href="/atlas" className="custom-button ms-md-auto">
                                                        Launch Atlas
                                                    </a>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {userCart?.attendees?.length > 0 && (
                                <div className="standard">
                                    <div>
                                        <h3>
                                            {userCart.activeEvent?.title ? `Event : ${userCart.activeEvent.title}` : 'Event : Not selected'}
                                        </h3>
                                    </div>
                                </div>
                            )}

                            {userCart?.attendees?.length > 0 && (<div className="research">
                                <div className="row">
                                    <div className="col-2 col-lg-3 col-xl-2 mb-2 mb-lg-4">
                                        <div className="image-wrap">
                                            <img src="/wp-content/themes/dom/assets/img/LBC-ticket-thumbnail.png"
                                                alt="Atlas Membership" />
                                        </div>
                                    </div>
                                    <div className="col-3 col-lg-9 col-xl-4 mb-2 mb-lg-4">
                                        <div className="research-one">
                                            <div>
                                                <h3>
                                                    Microsoft Licensing <br /> Training
                                                </h3>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-sm-4 col-lg-6 col-xl-3 mb-2 mb-lg-4">
                                        <div className="research-one">
                                            <div>
                                                {(() => {
                                                    const totalAttendees = userCart.attendees?.length || 0;
                                                    const assignedAttendees =
                                                        userCart.attendees?.filter((a) => a.leaveSeatUnassigned != "1").length || 0;
                                                    const remainingAttendees = totalAttendees - assignedAttendees;
                                                    if (totalAttendees === 0 && assignedAttendees === 0) {
                                                        return (
                                                            <div className="lnfo mb-1">
                                                                0 of 0 Attendees Assigned
                                                            </div>
                                                        );
                                                    }

                                                    return (
                                                        <>
                                                            <div className="lnfo mb-1">
                                                                {assignedAttendees} of {totalAttendees}{" "}
                                                                {totalAttendees === 1 ? "Attendee" : "Attendees"} Assigned
                                                            </div>

                                                            {remainingAttendees > 0 ? (
                                                                <p>
                                                                    You can still assign {remainingAttendees}{" "}
                                                                    {remainingAttendees === 1 ? "attendee" : "attendees"} later by{" "}
                                                                    <a href={`mailto:${supportEmail}?subject=Assign%20Attendees%20Request`}>emailing us</a>.
                                                                </p>
                                                            ) : (
                                                                <p>All attendees are assigned</p>
                                                            )}

                                                            {/* Assigned attendees list */}
                                                            <div className="assigned-attendees mt-2">
                                                                <h5>Assigned Attendees:</h5>
                                                                {userCart.attendees
                                                                    .filter((a) => a.leaveSeatUnassigned === "0")
                                                                    .map((a, i) => (
                                                                        <div key={i}>
                                                                            {a.firstName} {a.lastName} ({a.email})
                                                                        </div>
                                                                    ))}

                                                                {userCart.attendees.filter((a) => a.leaveSeatUnassigned === "0").length === 0 && (
                                                                    <div>No assigned attendees.</div>
                                                                )}
                                                            </div>
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>)}

                            <div className="payment-information">
                                <div className="payment-information-card">
                                    <h3 className="payment-information-heading">Purchased by</h3>
                                    <div className="row">
                                        <div className="col-sm-5 col-lg-4">
                                            <p className="public-desc">{im_custom.user.name}</p>
                                            <p className="public-desc">{im_custom.user.email}</p>
                                            <p className="public-desc">{im_custom.user.phone}</p>
                                        </div>
                                        <div className="col-sm-5 col-lg-4">
                                            <p className="public-desc">
                                                {im_custom.user.company} <br /> {im_custom.user.country}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                {/* Payment Method Details */}
                                {paymentMethodDetails && (<div className="payment-information-card">
                                    <h3 className="payment-information-heading">Payment Details</h3>
                                    <div className="row">
                                        <div className="col-sm-5 col-lg-4">
                                            {paymentMethodDetails?.card?.display_brand && (<p className="public-desc">
                                                Brand: {paymentMethodDetails?.card?.display_brand}
                                            </p>)}
                                            {paymentMethodDetails?.card?.last4 && (<p className="public-desc">
                                                Card Number: XXXX-XXXX-XXXX-{paymentMethodDetails?.card?.last4}
                                            </p>)}
                                            {paymentMethodDetails?.card?.exp_month && paymentMethodDetails?.card?.exp_year && (<p className="public-desc">
                                                Expiration: {paymentMethodDetails?.card?.exp_month}/{paymentMethodDetails?.card?.exp_year}
                                            </p>)}
                                        </div>
                                    </div>
                                </div>)}

                            </div>
                        </div>

                        <div className="col-xxl-3 col-md-4">
                            <OrderSummary
                                userCart={userCart}
                                im_custom={im_custom}
                                estimatedTax={taxAmount}
                                grandTotal={grandTotal}
                                continueButtonText={'Print Receipt'}
                                continueButtonHandle={handlePrint}
                            />
                            <div className="order-summary mt-3 text-center">
                                <h3>Have questions?</h3>
                                <a href="/form/?form_id=18&campaign=Thankyou_Contact_Us" className="custom-button w-100">
                                    Contact Us
                                </a>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </>
    );
};
const overlayStyle = {
    position: "fixed",
    top: "9%",
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
};

const popupStyle = {
    backgroundColor: "#fff",
    padding: "20px",
    borderRadius: "10px",
    width: "90%",
    maxWidth: "643px",
    // maxHeight: "90vh",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    height: "520px",

};
export default ThankYou;